import { __awaiter, __generator } from "tslib";
import { getErrorHandler } from '@office-start/error-handler';
import { addOnVisibilityChangeListener, getIsPageVisible, } from '@office-start/page-visibility-service';
import { mark as markPerformance } from '@office-start/performance-service';
import TinyQueue from 'tinyqueue';
import { createHandle, createNoopHandle } from './Handles';
import { heap } from './Heap';
import { cancelIdleCallbackPolyfill, requestIdleCallbackPolyfill, } from './IdleCallbackPolyfills';
import { logSchedulerMetrics } from './logSchedulerMetrics';
import { IDLE_VERYLOW_PRIORITY, IMMEDIATE_PRIORITY, } from './Models';
var copilotM365AppId = 'd870f6cd-4aa5-4d42-9626-ab690c041429';
// Handle to a queued idle callback, returned from "requestIdleCallback" API.
var idleCallbackHandle;
// A flag that determine if the page is visible or not. When the page is hidden,
// we try to drain the idle task queue ASAP since these tasks won't affect
// user perceived performance.
var isPageHidden = false;
var isSchedulingPausedForMOSApp = false;
var resumeTimeout = null;
var resumeSchedulingTimeout = 10000; // 10 seconds
var schedulingResumed = false; // Atomic flag to prevent race conditions
var queuedWork = new TinyQueue([], function (a, b) { return a.priority - b.priority; });
var shouldLogFirstIdle = getIsPageVisible(); // We cannot accurately log "First Idle" if tab is hidden at any point.
var shouldLogFirstSchedule = true;
function doWork(deadline) {
    while (isPageHidden || (deadline && deadline.timeRemaining() > 0)) {
        var scheduledWork = heap.pop();
        if (!scheduledWork) {
            break;
        }
        if (scheduledWork.cancelled) {
            continue;
        }
        try {
            if (isSchedulingPausedForMOSApp &&
                scheduledWork.priority === IDLE_VERYLOW_PRIORITY) {
                // If scheduling is paused, we should not run the task.
                // Instead, we should queue it up for later.
                queuedWork.push(scheduledWork);
                return;
            }
            scheduledWork.work(deadline);
            scheduledWork.completed = true;
        }
        catch (ex) {
            var errorHandler = getErrorHandler();
            errorHandler.handleError(ex, "DXUlK" /* ErrorId.DXUlK */);
        }
    }
    // If we run out of time, but there are more tasks to do, schedule them for later.
    if (heap.peek()) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        dequeueWhenIdle();
    }
}
addOnVisibilityChangeListener(function (visibilityState) {
    isPageHidden = visibilityState !== 'visible';
    if (isPageHidden) {
        shouldLogFirstIdle = false;
        if (idleCallbackHandle) {
            // When the user hides the page, cancel any queued idle tasks, and run them now.
            cancelIdleCallbackPolyfill(idleCallbackHandle);
            idleCallbackHandle = null;
        }
        doWork();
    }
});
function dequeueWhenIdle() {
    // When the page is hidden, we can immediately begin processing tasks.
    if (isPageHidden) {
        doWork();
    }
    // If there is already an IdleCallback handle, we have already scheduled
    // work to happen when idle.
    if (idleCallbackHandle) {
        return;
    }
    // Schedule work to run when idle.
    idleCallbackHandle = requestIdleCallbackPolyfill(function (deadline) {
        if (shouldLogFirstIdle) {
            shouldLogFirstIdle = false;
            markPerformance('First_Idle_React');
        }
        idleCallbackHandle = null;
        doWork(deadline);
    });
}
export function scheduleWork(work, priority, workName) {
    if (priority === IMMEDIATE_PRIORITY) {
        work();
        return createNoopHandle();
    }
    if (shouldLogFirstSchedule) {
        shouldLogFirstSchedule = false;
        markPerformance('First_Schedule_React');
    }
    var scheduledWork = {
        work: work,
        priority: priority,
        cancelled: false,
        completed: false,
        workName: workName,
    };
    if (isSchedulingPausedForMOSApp && priority === IDLE_VERYLOW_PRIORITY) {
        // Queue the work if scheduling is paused
        queuedWork.push(scheduledWork);
        return createNoopHandle();
    }
    var workHandle = createHandle(scheduledWork);
    heap.push(scheduledWork);
    dequeueWhenIdle();
    return workHandle;
}
export function handlePauseScheduling() {
    pauseScheduling();
}
function pauseScheduling() {
    if (resumeTimeout) {
        return;
    }
    pauseAndWaitForEventOrTimeout();
}
export function handleResumeScheduling() {
    if (!schedulingResumed) {
        schedulingResumed = true;
        resumeScheduling();
    }
}
function resumeScheduling() {
    if (resumeTimeout) {
        clearTimeout(resumeTimeout);
        resumeTimeout = null;
    }
    isSchedulingPausedForMOSApp = false;
    processQueuedWork().then(function () {
        schedulingResumed = false;
    });
    dequeueWhenIdle();
}
function processQueuedWork() {
    return __awaiter(this, void 0, void 0, function () {
        var taskPromises, _loop_1;
        var _this = this;
        return __generator(this, function (_a) {
            taskPromises = [];
            _loop_1 = function () {
                var work = queuedWork.pop();
                if (work) {
                    var taskPromise = (function () { return __awaiter(_this, void 0, void 0, function () {
                        var error_1, errorHandler;
                        return __generator(this, function (_a) {
                            switch (_a.label) {
                                case 0:
                                    _a.trys.push([0, 4, 5, 6]);
                                    if (!!work.cancelled) return [3 /*break*/, 2];
                                    return [4 /*yield*/, work.work()];
                                case 1:
                                    _a.sent();
                                    return [3 /*break*/, 3];
                                case 2:
                                    work.cancelled = true;
                                    _a.label = 3;
                                case 3: return [3 /*break*/, 6];
                                case 4:
                                    error_1 = _a.sent();
                                    errorHandler = getErrorHandler();
                                    errorHandler.handleError(error_1, "DXUlK" /* ErrorId.DXUlK */);
                                    return [3 /*break*/, 6];
                                case 5:
                                    work.completed = true;
                                    if (work.workName) {
                                        logSchedulerMetrics({
                                            appId: copilotM365AppId,
                                            scenarioName: work.workName,
                                            priority: work.priority,
                                        });
                                    }
                                    return [7 /*endfinally*/];
                                case 6: return [2 /*return*/];
                            }
                        });
                    }); })();
                    taskPromises.push(taskPromise);
                }
            };
            while (queuedWork.length > 0) {
                _loop_1();
            }
            Promise.allSettled(taskPromises);
            return [2 /*return*/];
        });
    });
}
export function pauseAndWaitForEventOrTimeout() {
    if (isSchedulingPausedForMOSApp) {
        if (resumeTimeout) {
            return;
        }
    }
    isSchedulingPausedForMOSApp = true;
    resumeTimeout = setTimeout(function () {
        handleResumeScheduling();
    }, resumeSchedulingTimeout);
}
//# sourceMappingURL=Scheduler.js.map